import { StaffDetails, StaffViewInfo } from '@libs/models/talents/talent';
import { request } from '../request';
import dayjs from 'dayjs';

export async function getInternalTalent(id: string): Promise<StaffDetails> {
  const response = await request.get<GetInternalTalentResponse>(`/v2/internal/staff/${id}`);
  return {
    ...response.data,
    type: 'internal',
    fullName: [response.data.firstName, response.data.lastName].filter(Boolean).join(' '),
    qualificationPayRates: response.data.qualificationPayRates.map((item) => ({
      ...item,
      specialtyPayRates: item.specialties.map((specialty) => ({
        ...specialty,
        specialty: {
          ...specialty.specialty,
        },
      })),
    })),
    orientedList: (response.data.orientedRecords ?? []).map((o) => ({
      locationId: o.locationId,
      value: o.oriented === 'Yes',
    })),
    qualifications: response.data.qualificationPayRates.map((q) => ({
      qualification: q.qualification,
      specialties: q.specialties.map((s) => s.specialty),
    })),
    status: response.data.staffingStatus,
    lastWorkedTime: response.data.lastWorkedTime ? dayjs(response.data.lastWorkedTime) : dayjs(),
    contractType: response.data.contractType,
    credentials: response.data.credentials
      ? response.data.credentials.map((c) => ({
          ...c,
          type: c.type,
          name: c.fileName,
          url: c.filePath,
        }))
      : [],
  };
}
type GetInternalTalentResponse = {
  id: string; // 'staffmember-01k36pcpkqyqvq6zzyvpsa52wt';
  imageUrl: string; // 'https://picsum.photos/640/480/?image=553';
  firstName: string; // 'Carey';
  lastName: string; // 'Howell';
  phoneNumber: string; // '+1 (158) 629-4212';
  email: string; // 'Antonio_Witting60@gmail.com';
  address: {
    firstAddress: string; // '8823 Jevon Flat';
    city: string; // 'Port Maceyfort';
    state: string; // 'ME';
    zipCode: string; // '39620';
    secondAddress: string; // 'Suite 185';
  };
  orientedRecords: {
    locationId: string; // 'location-01k6rvfdgyqz3772fdfgz3ht5f';
    oriented: string; // 'Yes';
  }[];
  hrInfo: {
    primaryRegion: {
      city: string; // 'Port Cole';
      state: string; // 'ID';
    };
    secondaryRegions: {
      city: string; // 'New Johannberg';
      state: string; // 'IN';
    }[];
  };
  jobTitle: string; // 'Central Interactions Assistant';
  location: {
    id: string; // 'location-01k36pcpkq22kcfvthgx2ckf25';
    name: string; // 'Braun - Witting Hospital';
  };
  qualificationPayRates: {
    qualification: {
      id: string; // 'nursequalification-01k36pcpkq87fzw444gsnvazjh';
      name: string; // 'Name';
      abbreviation: string; // 'Abbreviation';
      category: string; // 'Category';
    };
    rateType: 'Hourly' | 'Monthly' | 'Yearly';
    rate: number; // 0;
    specialties: {
      specialty: {
        id: string; // 'speciality-01k36pcpkqs4mjyc49jz1tqee7';
        name: string; // 'Name';
        category: string; // 'Category';
      };
      rate: number; // 10;
    }[];
  }[];
  employmentType:
    | 'Contract'
    | 'SalariedPartTime'
    | 'SalariedFullTime'
    | 'HourlyPartTime'
    | 'HourlyFullTime'
    | 'PerDiemOrPRN';
  availableForTravelDistance: number; // 10;
  workShifts: {
    dayOfWeek: 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
    startTime: string; // '08:00:00';
    duration: string; // '08:00:00';
  }[];
  staffingStatus: 'Active' | 'Deactivated';
  lastWorkedTime: string; // '2025-08-21T16:11:33.8795555Z';
  contractType: 'W2' | 'TenNinetyNine';
  credentials: {
    type: 'License' | 'Certification' | 'BackgroundCheck' | 'HealthRecord' | 'Assessment' | 'EDocument';
    fileName: string; //  'File Name.pdf'; - any human-readable name (not the physical file name)
    filePath: string; //  'https://example.com/file.pdf';
  }[];
};

export async function getExternalTalent(id: string): Promise<StaffViewInfo> {
  const response = await request.get<GetExternalTalentResponse>(`/v2/external/staff/${id}`);
  return {
    ...response.data,
    type: 'external',
    fullName: [response.data.firstName, response.data.lastName].filter(Boolean).join(' '),
    status: response.data.staffingStatus,
    orientedList: (response.data.orientedRecords ?? []).map((o) => ({
      locationId: o.locationId,
      value: o.oriented === 'Yes',
    })),
    lastWorkedTime: response.data.lastWorkedTime ? dayjs(response.data.lastWorkedTime) : dayjs(),
    credentials: response.data.credentials
      ? response.data.credentials.map((c) => ({
          ...c,
          type: c.type,
          name: c.fileName,
          url: c.filePath,
        }))
      : [],
  };
}
type GetExternalTalentResponse = {
  id: string; // 'staffmember-01k6rvfdgy5n405yr4yxk6jfv8';
  firstName: string; // 'Carey';
  lastName: string; // 'Howell';
  imageUrl: string; // 'https://picsum.photos/640/480/?image=553';
  phoneNumber: string; // '+1 (158) 629-4212';
  email: string; // 'Antonio_Witting60@gmail.com';
  jobTitle: string; // 'Central Interactions Assistant';
  location: {
    id: string; // 'location-01k6rvfdgyqz3772fdfgz3ht5f';
    name: string; // 'Braun - Witting Hospital';
  };
  employmentType:
    | 'Contract'
    | 'SalariedPartTime'
    | 'SalariedFullTime'
    | 'HourlyPartTime'
    | 'HourlyFullTime'
    | 'PerDiemOrPRN';
  orientedRecords: {
    locationId: string; // 'location-01k6rvfdgyqz3772fdfgz3ht5f';
    oriented: string; // 'Yes';
  }[];
  agency: {
    id: string; // 'example.site.com';
    name: string; // 'Braun - Witting Agency';
    agencyTenantId: string;
    logoUrl: string; // 'https://www.samplestaff.com/agency/logo';
  };
  qualifications: {
    qualification: {
      id: string; // 'nursequalification-01k6rvfdgydcje63kgyxycyv5q';
      name: string; // 'Name';
      abbreviation: string; // 'Abbreviation';
      category: string; // 'Category';
    };
    specialties: [
      {
        id: string; // 'speciality-01k6rvfdgyvk28kwv0126d7cw4';
        name: string; // 'Name';
        category: string; // 'Category';
      },
    ];
  }[];
  staffingStatus: 'Active' | 'Deactivated';
  lastWorkedTime: string; // '2025-10-05T00:13:51.0060746Z';
  credentials: {
    type: 'License' | 'Certification' | 'BackgroundCheck' | 'HealthRecord' | 'Assessment' | 'EDocument';
    fileName: string; //  'File Name.pdf'; - any human-readable name (not the physical file name)
    filePath: string; //  'https://example.com/file.pdf';
  }[];
};
